// @ts-strict-ignore
import React from 'react';
import {createRoot} from 'react-dom/client';
import ace from 'brace';
import stringify from 'json-stable-stringify';
import isEmpty from 'lodash/isEmpty';
import {AttrsSchema, Component} from 'panel';

import 'common/util/ace/patches';
import 'common/util/ace/theme/mixpanel';
import {QUERY_BUILDER_RESOURCE_TYPE_MAPPING} from 'common/widgets/property-filter-menu/models/insights-filter';
import {propertiesUtils} from 'common/report/util/properties';
import {StackableComponent} from 'common/components/base/stackable';
import {QueryBlockReact} from 'common/query-builder/query-block';
import {MenuType, QueryBlockParams} from 'common/query-builder/query-block/types';
import {QueryBlockThemeExtras} from 'common/query-builder/query-block/constants';
import arbselectorDefs from 'common/util/tern/definitions/arb-selector.json';
import {customPropertiesValidateClient} from 'common/report/queries/query-api/custom-properties-validate-client';
import {getAlphaLabelFromIndex, MeasureUtil} from 'common/util';
import mpFonts from 'common/stylesheets/mixins/fonts.json';
import {
  IncludeCustomPropertyType,
  Property,
  SelectedPropertyItem,
  TypecastPillAction,
} from 'common/query-builder/query-block/properties-menu/types';
import {customPropertiesStore} from 'common/report/stores/custom-properties';
import {PropertyType, ResourceType} from 'common/report/constants';
import {defineAndMakeJsxFactory} from 'common/util/jsx';
import {getPropertySelectionFromProperty} from 'common/query-builder/query-entry-section/util/behavior';
import {segmentationToPropertySelection} from 'common/query-builder/query-block/segment-menu/util';
import {SegmentValue} from 'common/query-builder/query-block/segment-menu/types';
import {getSelectedLabel, getSelectedLeftIcon} from 'common/query-builder/query-block/properties-menu/util';
import {BORROW_PROPERTY} from 'common/query-builder/query-block/properties-menu/constants';
import type {Translatable} from 'common/i18n';
import {IronContextRegistry} from 'common/report/context-registry';
import {LocalizationStore} from 'common/i18n/store';

import {CustomPropertyCompleter} from './completer';

const {Autocomplete} = ace.acequire(`ace/autocomplete`);
const {TokenIterator} = ace.acequire(`ace/token_iterator`);
const range = ace.acequire(`ace/range`);

type QueryBlockWrapper = HTMLDivElement & {
  setProps: (props: Partial<QueryBlockParams>) => void;
};

type AnnotationType = {
  first_line: number;
  first_column: number;
  last_line: number;
  last_column: number;
  message: string;
};

type PropertyEntry = {
  label: Property[`label`];
  propertyDefaultType: Property[`propertyDefaultType`];
  resourceType: Property[`resourceType`];
  type: Property[`type`];
  value: Property[`value`];
  unit?: Property[`unit`];
  dataGroupId?: Property[`dataGroupId`];
};

type PropertyTokenSpec = {
  range: ace.Range;
  type: string;
  tokenText: string;
  alphaLabel: string;
};

export type ComposedPropertiesMap = Record<string, PropertyEntry>;

export type ChangeEventDetail = {
  composedProperties: ComposedPropertiesMap;
  displayFormula: string;
  extractionError: any;
};

const CLASS_SYNTAX_ERROR = `ace-highlight-syntax-error`;
const CLASS_PROPERTY_DROPDOWN = `ace_fold_property_dropdown`;

const DEFAULT_CHARACTER_WIDTH = 8.802111206054688;

const HOVERED_FOLD_PREFIX = `*`;
const PROPERTY_TOKEN_PREFIX = `@"""`;
const PROPERTY_TOKEN_SUFFIX = `"""@`;

function getTokenTextForEncodedProperty(encodedProperty: string): string {
  return `${PROPERTY_TOKEN_PREFIX}${encodedProperty}${PROPERTY_TOKEN_SUFFIX}`;
}

function getPropertyFromTokenText(tokenText: string): PropertyEntry {
  const encodedProperty = tokenText.substring(
    PROPERTY_TOKEN_PREFIX.length,
    tokenText.length - PROPERTY_TOKEN_SUFFIX.length,
  );
  return JSON.parse(encodedProperty) as PropertyEntry;
}

const EMPTY_PROPERTY_TOKEN = getTokenTextForEncodedProperty(`{}`);
export const PERSISTENT_EMPTY_PROPERTY_TOKEN = getTokenTextForEncodedProperty(`{ }`);

export const CUSTOM_PROPERTY_RESOURCE_TYPES = {
  [ResourceType.Event]: QUERY_BUILDER_RESOURCE_TYPE_MAPPING[ResourceType.Event],
  [ResourceType.User]: QUERY_BUILDER_RESOURCE_TYPE_MAPPING[ResourceType.User],
};

let autocompleteSingleton = null;

function escapeHtml(text) {
  const textElem = document.createTextNode(text);
  const pElem = document.createElement(`p`);
  pElem.appendChild(textElem);
  return pElem.innerHTML;
}

// property.label is trustworthy when the property is first generated
// by an mp-query-block, but if the user saves a custom property and
// comes back to it the label may be out of date (e.g. it may refer to
// a custom property which has been renamed), in cases where we need
// to make sure that we have the most up to date property name, this
// function will recompute the property name
function computePropertyName(property, i18n: LocalizationStore) {
  // get the latest copy of the custom property, not the stored copy
  const customProperty = property.customPropertyId ? customPropertiesStore.value[property.customPropertyId] : null;
  let parts: string[] = [property.name || property.value];

  if (Array.isArray(property.propertyObjectKey)) {
    parts = [...parts, ...property.propertyObjectKey];
  }

  parts = parts.map((value) =>
    propertiesUtils.renameProperty(value, property.resourceType, i18n, {
      dataGroupId: property.dataGroupId,
      customProperty,
    }),
  );
  return parts.join(`.`);
}

function isPropertyToken(tokenType: string): boolean {
  return tokenType.includes(`encoded_property`);
}

/*
 * This class is a wrapper around Ace editor meant for writing selector-language formulas.
 * For the most part it uses standard Ace APIs to make this experience work, but here are
 * some of the more complex things it does:
 * - Inlining property-drop-downs via Ace editor folds: When writing a formula with this
 *   editor users can insert mp-query-block components inline in the editor which represent
 *   properties. Inlining components in the text editor is not something Ace supports
 *   natively. In the Ace Document these inline components are recorded by a string that
 *   matches the 'encoded_property' token in the selector tokenizer. To render mp-query-blocks
 *   in place of this text we interfere with Aces pre and post render behavior ("render" here
 *   refers to Ace's construction of the <span>s that make up the text of the document.
 *   In order to properly resize the 'encoded_property' text to the size of the mp-query-block
 *   we have a pre-render hook which re-configured Ace's fold-management to "fold" the
 *   encoded_property text into a smaller size. In order to render the mp-query-blocks
 *   we have a post-render hook which searches the DOM for <span>s with the ace_fold class
 *   and creates mp-query-block components which are absolutely-positioned in the same
 *   location on the page. This means: We cannot enable Ace's real code-folding support
 *   as we have hijacked it's functionallity for another purpose. We cannot enable a
 *   scrollbar for the text box since the mp-query-builders are not children of ace's
 *   scroller component (for overflow reasons).
 */
export class Editor extends Component<EmptyObj, any, unknown, unknown, IronContextRegistry> {
  declare editor: any; // ace.Editor
  declare idleTimeout: ReturnType<typeof setTimeout>;
  declare ready: boolean;

  declare lastHoveredFold: any;
  declare emptyMessageElem: HTMLElement | null;
  declare composedProperties: ComposedPropertiesMap;
  /**
   * Map of composed properties that could not be found in the project.
   * The key is the alpha key (e.g. _A, _B, _C). **Not** the actual property name.
   */
  invalidComposedPropertyKeySet: Set<string> = new Set();

  // used for P3 tracking to see if the user interacted with the formula box
  declare hadInteraction: boolean;

  static get attrsSchema(): AttrsSchema<any> {
    return {
      ...super.attrsSchema,
      'all-data-groups': `boolean`,
      'composed-properties': {
        type: `string`,
        default: `{}`,
      },
      'data-group-id': `string`,
      'display-formula': `string`,
      'event-names': {
        type: `json`,
        default: null,
        description: `when set, displayed properties belong only to the set of events passed`,
      },
      'enable-workspace-filter-restrictions': `boolean`,
      'show-borrowed-properties': `boolean`,
      'project-id': `number`,
      'resource-type': {
        enum: Object.values(CUSTOM_PROPERTY_RESOURCE_TYPES),
        required: true,
        type: `string`,
      },
      'track-name': `string`,
      'user-id': `number`,
      'workspace-id': `number`,
      'show-borrow-property-action': `boolean`,
    };
  }

  get config() {
    return {
      ...super.config,
      contexts: [`i18n` as const],
    };
  }

  get t() {
    return this.getContext(`i18n`).t;
  }

  setDisplayFormula(displayFormula, properties) {
    this.populateDisplayFormula(displayFormula, properties);
  }

  setComposedProperties(properties) {
    this.composedProperties = properties;
    this.onChange();
  }

  private monkeyPatchFoldLogic() {
    const {print} = this.getContext(`i18n`);

    // We need to disable Ace's normal fold management (e.g. clicking on a fold automatically
    // expands it) to stop it from conflicting with how we abuse it to resize encoded_property
    // text. Here we replace some fold management functions with no-op functions and save copies
    // of the original functions for our own use with modified names.
    this.editor.getSession().removeFoldReal = this.editor.getSession().removeFold;
    this.editor.getSession().addFoldReal = this.editor.getSession().addFold;
    this.editor.getSession().removeFold = function (_fold) {
      // disable normal fold removal logic when ace calls it
      // when we remove folds in our own code we use removeFoldReal()
    };
    this.editor.getSession().expandFold = function (_fold) {
      // disable normal fold expansion logic when ace calls it
    };
    this.editor.getSession().addFold = function (_fold) {
      // disable normal fold addition logic when ace calls it
    };

    // We monkey patch ace's rendering of folds so that we can render a pil with the property name
    // and icon instead. For "blank" properties and the last hovered property (the ones the user can
    // click and interact with) we attach a special class (CLASS_PROPERTY_DROPDOWN). There is logic
    // in `onAfterRender` to overlay `mp-query-block`s on these folds.
    const realRenderToken = this.editor.renderer.$textLayer.$renderToken.bind(this.editor.renderer.$textLayer);
    this.editor.renderer.$textLayer.$renderToken = (stringBuilder, screenColumn, token, value) => {
      if (token.type === `fold`) {
        let hovered = false;
        let key: string = token.value.trim();
        if (key[0] === HOVERED_FOLD_PREFIX) {
          hovered = true;
          key = key.substring(1);
        }
        const property = this.composedProperties[key];
        const width = `${token.value.length * this.editor.renderer.$textLayer.config.characterWidth}px`;
        if (!hovered && property.label) {
          const icon = this.getPropertyButtonIcon(property);
          // NOTE: the spaces before svg-icon are important for the alignment of the label in the pill
          const iconHtml = icon ? `  ` + `<svg-icon icon="${icon}"></svg-icon>` : ``;
          const foldTextHtml = `<span class="ace_fold_text">${escapeHtml(print(this.getPropertyLabel(property)))}</span>`;
          stringBuilder.push(`<span class="ace_fold" style="width:${width};">`, iconHtml, foldTextHtml, `</span>`);
        } else {
          stringBuilder.push(`<span class="ace_fold `, CLASS_PROPERTY_DROPDOWN, `" style="width:`, width, `;"></span>`);
        }
        return screenColumn + value.length;
      }
      return realRenderToken(stringBuilder, screenColumn, token, value);
    };
  }

  private updateEmptyMessage() {
    const value = this.editor.getValue();
    if (value === `` && !this.emptyMessageElem) {
      const emptyMessageElem = document.createElement(`div`);
      emptyMessageElem.className = `ace_empty_message`;
      emptyMessageElem.textContent = this.t(`custom-property.formula-placeholder`);
      this.emptyMessageElem = emptyMessageElem;
      this.editor.renderer.content.appendChild(this.emptyMessageElem);
    } else if (value !== `` && this.emptyMessageElem) {
      this.emptyMessageElem.remove();
      this.emptyMessageElem = null;
    }
  }

  focus() {
    this.editor.focus();
  }

  canShowBorrowPropertyAction(): boolean {
    return this.attr(`show-borrow-property-action`);
  }

  disconnectedCallback() {
    StackableComponent.deleteCurrentStackingContextTarget(this.toString());
  }

  // TODO opslead
  connectedCallback() {
    this.innerHTML = ``;
    const editorEl = document.createElement(`div`);
    editorEl.appendChild(document.createTextNode(``));
    this.editor = ace.edit(editorEl);
    this.initializeEditorTheme();
    this.idleTimeout = null;

    this.hadInteraction = false;

    this.editor.$blockScrolling = Infinity;
    this.editor.setOptions({
      displayIndentGuides: false,
      enableBasicAutocompletion: true,
      enableLiveAutocompletion: true,
      highlightActiveLine: false,
      maxLines: `Infinity`,
      showFoldWidgets: false,
      showGutter: true,
      showPrintMargin: false,
      wrap: true,
    });
    this.editor.completers = [new CustomPropertyCompleter()];

    // syntax check worker must be disabled for our own use of setAnnotations to work
    this.editor.getSession().setUseWorker(false);
    this.editor.getSession().setUseWrapMode(true);
    this.editor.container.style.lineHeight = 1.91;
    this.editor.renderer.setPadding(10);
    this.editor.getSession().setMode(`ace/mode/arb-selector`);
    this.editor.getSession().setTabSize(2);
    this.editor.on(`blur`, () => {
      this.dispatchEvent(
        new CustomEvent(`blur`, {
          detail: {
            height: this.editor.getSession().getScreenLength() * (this.editor.renderer.lineHeight || 1.9),
          },
        }),
      );
    });
    this.editor.on(`change`, this.onChange.bind(this));
    this.editor.on(`focus`, () => {
      this.dispatchEvent(new CustomEvent(`focus`, {}));
    });
    this.editor.on(`blur`, () => {
      this.dispatchEvent(
        new CustomEvent(`autocompleteChange`, {
          detail: {
            menuTooltipData: null,
            cursorTooltipData: null,
          },
        }),
      );
    });
    this.editor.on(`mousemove`, (ev) => {
      // We replace the last hovered fold's normal simple div with an mp-query-block
      // so that it is editable, shows tooltips, etc. This callback is responsible for
      // editing the placeholder for the last hovered fold to begin with the `*`
      // character which is used as in indicator in the `renderToken` function that
      // this fold should render a query block instead of the normal div.
      const pos = ev.getDocumentPosition();
      const fold = this.editor.getSession().getFoldAt(pos.row, pos.column, 1);
      if (fold && this.lastHoveredFold !== fold) {
        if (this.lastHoveredFold) {
          const placeholder = `${this.lastHoveredFold.placeholder.substring(HOVERED_FOLD_PREFIX.length)} `;
          const foldRange = this.lastHoveredFold.range.clone();
          this.editor.getSession().removeFoldReal(this.lastHoveredFold);
          this.editor.getSession().addFoldReal(placeholder, foldRange);
        }
        const placeholder = `${HOVERED_FOLD_PREFIX}${fold.placeholder.trim()}`.padEnd(fold.placeholder.length, ` `);
        const foldRange = fold.range.clone();
        this.editor.getSession().removeFoldReal(fold);
        this.lastHoveredFold = this.editor.getSession().addFoldReal(placeholder, foldRange);
        this.editor.renderer.updateText();
      }
    });

    this.appendChild(editorEl);

    if (this.getAttribute(`display-formula`) !== ``) {
      const composedProperties = JSON.parse(this.getAttribute(`composed-properties`));
      this.populateDisplayFormula(this.getAttribute(`display-formula`), composedProperties);
      this.editor.completers[0].setComposedProperties(this.getAttribute(`project-id`), composedProperties);
    }
    this.editor.clearSelection();

    if (!autocompleteSingleton) {
      autocompleteSingleton = new Autocomplete();
    }
    this.editor.completer = autocompleteSingleton;
    this.editor.completer.editor = this.editor;

    const popup = this.editor.completer.getPopup();
    popup.renderer.on(`afterRender`, () => {
      // item being actively hovered with mouse takes priority,
      // otherwise use item currently selected via keyboard
      const row = popup.getHoveredRow() === -1 ? popup.getRow() : popup.getHoveredRow();
      const data = popup.getData(row);
      this.dispatchEvent(
        new CustomEvent(`autocompleteChange`, {
          detail: {
            autocompleteOpen: true,
            menuTooltipData: data && data.tooltip ? data.tooltip : null,
          },
        }),
      );
    });
    popup.on(`show`, () => {
      StackableComponent.setCurrentStackingContextTarget(this.toString());
    });
    popup.on(`hide`, () => {
      StackableComponent.deleteCurrentStackingContextTarget(this.toString());
      this.dispatchEvent(
        // clear the documentation tooltip if the autocomplete popup goes away
        new CustomEvent(`autocompleteChange`, {
          detail: {
            autocompleteOpen: false,
          },
        }),
      );
    });

    this.editor.getSession().selection.on(`changeCursor`, (_ev) => {
      const currFunc = this.getCurrentFunctionInfo(this.editor.getCursorPosition());
      let completions = null;
      if (currFunc) {
        completions = Object.keys(arbselectorDefs)
          .filter((key) => key.split(`(`)[0].toLowerCase() === currFunc.token.value.toLowerCase())
          .map((key) => ({
            header: key,
            subheader: arbselectorDefs[key][`syntax`],
            description: arbselectorDefs[key][`!doc`],
            details: [{icon: `info`, name: `Example`, value: arbselectorDefs[key][`examples`]}],
          }));
      }
      if (completions && completions.length > 0) {
        this.dispatchEvent(
          new CustomEvent(`autocompleteChange`, {
            detail: {
              currentAutocompleteLine:
                this.editor.container.getElementsByClassName(`ace_gutter-layer`)[0].children[currFunc.parenPos.row],
              cursorTooltipData: completions[0],
            },
          }),
        );
      } else {
        this.dispatchEvent(
          new CustomEvent(`autocompleteChange`, {
            detail: {
              cursorTooltipData: null,
            },
          }),
        );
      }
    });

    this.monkeyPatchFoldLogic();

    this.editor.on(`change`, this.updateEmptyMessage.bind(this));
    this.updateEmptyMessage();

    this.editor.renderer.on(`afterRender`, this.onAfterRender.bind(this));
    this.editor.commands.addCommand({
      name: `addProperty`,
      bindKey: {win: `.`, mac: `.`},
      exec(editor) {
        const cursorPosition = editor.getCursorPosition();
        const token = editor.getSession().getTokenAt(cursorPosition.row, cursorPosition.column);
        if (
          !token ||
          (token.type !== `string.quoted` && token.type !== `comment` && token.type !== `constant.numeric`)
        ) {
          editor.session.insert(editor.getCursorPosition(), EMPTY_PROPERTY_TOKEN);
        } else {
          editor.session.insert(editor.getCursorPosition(), `.`);
        }
      },
    });

    this.dispatchEvent(
      new CustomEvent(`connect`, {
        detail: {
          autocompletePopup: popup.container,
          editor: this,
        },
      }),
    );

    this.ready = true;
    this.onChange();
  }

  initializeEditorTheme(): void {
    this.editor.setTheme(`ace/theme/mixpanel`);
    // this is hacky, but ace editor injects the style tags into the global space so we grab them
    // from there and inject the stylesheets into the style tag in the shadowdom we create here
    const aceCss = [`ace_editor.css`, `ace-tm`, `ace-mixpanel`]
      .map((id) => document.getElementById(id)?.innerHTML)
      .join(` `);
    const styleTag = document.createElement(`style`);
    // this style content is already present in the page, we are pushing the content into the shadow dom
    // eslint-disable-next-line no-unsanitized/property
    styleTag.innerHTML = aceCss;
    this.el.appendChild(styleTag);
  }

  private onChange() {
    // do not fire change events while component is still being setup
    if (!this.ready) {
      return;
    }

    this.recomputeFolds();
    this.scheduleSyntaxChecking();
    const data = this.extractDisplayFormula();
    if (data.persistableFormula !== ``) {
      this.hadInteraction = true;
    }
    this.editor.completers[0].setComposedProperties(this.getAttribute(`project-id`), data.properties);
    const changeEventDetail: ChangeEventDetail = {
      composedProperties: data.properties,
      displayFormula: data.persistableFormula,
      extractionError: data.errors.length > 0 ? data.errors[0].message : null,
    };
    this.dispatchEvent(
      new CustomEvent(`change`, {
        detail: changeEventDetail,
      }),
    );
  }

  private createQueryBlock(property: PropertyEntry): QueryBlockWrapper {
    const wrapper = document.createElement(`div`) as QueryBlockWrapper;
    const root = createRoot(wrapper);

    let allDataGroups = this.attr(`all-data-groups`);
    let dataGroupId = this.attr(`data-group-id`);
    const currData = this.extractDisplayFormula();
    const currProps = Object.values(currData.properties);
    // if group prop is selected, make only the selected data group props selectable
    const groupProp = currProps.find((prop) => !!prop.dataGroupId);
    if (groupProp) {
      dataGroupId = groupProp.dataGroupId;
    }
    // if user prop is selected, make group props not selectable
    const userProp = currProps.find((prop) => prop.resourceType === ResourceType.User && !prop.dataGroupId);
    if (userProp) {
      allDataGroups = false;
    }

    let props: QueryBlockParams = {
      allDataGroups,
      dataGroupId,
      allowTypecastPillAction: TypecastPillAction.Typecast,
      eventNames: this.attr(`event-names`),
      enableAllDataGroupsForScdProps: true,
      enableWorkspaceFilterRestrictions: this.attr(`enable-workspace-filter-restrictions`),
      hideEditControls: true,
      includeCustomProperties: IncludeCustomPropertyType.PersistedOnly,
      showBorrowedProperties: this.attr(`show-borrowed-properties`),
      ...(this.canShowBorrowPropertyAction() &&
        this.attr(`show-borrowed-properties`) && {otherGroupByOptions: [BORROW_PROPERTY]}),
      inputOnly: !!property.label,
      label: this.getPropertyLabel(property) || property.label || ``,
      // don't show the resource type prefix (e.g. "User >") before property name
      prefixLabel: ``,
      projectHasCohorts: false,
      hideEvents: this.attr(`resource-type`) !== QUERY_BUILDER_RESOURCE_TYPE_MAPPING[ResourceType.Event],
      projectHasPeople:
        // in workspace filters you cannot mix event and user properties
        !this.attr(`enable-workspace-filter-restrictions`) ||
        (this.attr(`resource-type`) === QUERY_BUILDER_RESOURCE_TYPE_MAPPING[ResourceType.User] && !dataGroupId),
      projectId: this.attr(`project-id`),
      theme: QueryBlockThemeExtras.Builder,
      selected: property,
      selectorIcon: this.getPropertyButtonIcon(property) || ``,
      showAllTab: true,
      trackName: this.attr(`track-name`),
      type: MenuType.Properties,
      userId: this.attr(`user-id`),
      workspaceId: this.attr(`workspace-id`),
      onSelect: (detail: SelectedPropertyItem) => {
        // @ts-ignore
        const foldElement: HTMLElement = wrapper.foldElement;
        if (foldElement) {
          let propertyEntry: PropertyEntry;

          if (this.canShowBorrowPropertyAction()) {
            const segmentation = getPropertySelectionFromProperty(detail, null) as SegmentValue;
            propertyEntry = {
              ...(segmentationToPropertySelection(segmentation) as Property),
              // label is removed by segmentationToPropertySelection, but is expected in this component
              label: (detail.item as Property).label,
            };
          } else {
            propertyEntry = detail.item as PropertyEntry;
          }

          if (propertyEntry.type === PropertyType.Datetime && `unit` in propertyEntry) {
            delete propertyEntry.unit;
          }

          const foldRef = this.foldFromElement(foldElement);
          this.replaceRange(foldRef.range, getTokenTextForEncodedProperty(stringify(propertyEntry)));
        }
      },
      onClose: () => {
        // @ts-ignore
        const foldElement: HTMLElement = wrapper.foldElement;
        // if we are closing the editor and there is no selected property
        // remove the query block widget completely
        if (foldElement) {
          // `select` may have fired first, in which case the fold will
          // already have been replaced and will not be found
          const foldRef = this.foldFromElement(foldElement);
          if (foldRef) {
            const doc = this.editor.getSession().getDocument();
            if (doc.getTextRange(foldRef.range) === EMPTY_PROPERTY_TOKEN) {
              doc.remove(foldRef.range);
            }
          }
        }
        // return focus to the editor after the drop-down closes
        this.editor.focus();
      },
    };

    const render = (queryBlockProps: QueryBlockParams) => {
      root.render(<QueryBlockReact {...queryBlockProps} />);
    };

    wrapper.setProps = (newProps: Partial<QueryBlockParams>) => {
      props = {...props, ...newProps};
      render(props);
    };

    render(props);

    wrapper.style.position = `absolute`;

    // @ts-ignore - TODO: don't arbitrarily add properties
    wrapper.foldElement = null;

    return wrapper;
  }

  // TODO opslead
  private onAfterRender(_ev, renderer) {
    const cont = this.editor.container.parentElement;

    const queryBlockContainerClass = `mp-query-block-container`;
    let buttonDiv = cont.querySelector(`.${queryBlockContainerClass}`);
    if (!buttonDiv) {
      buttonDiv = document.createElement(`div`);
      buttonDiv.classList.add(queryBlockContainerClass);
      cont.appendChild(buttonDiv);
    }

    const contRect = cont.getBoundingClientRect();
    const folds = renderer.$textLayer.element.querySelectorAll(`.${CLASS_PROPERTY_DROPDOWN}`);
    folds.forEach((fold, idx) => {
      const foldRef = this.foldFromElement(fold);
      const foldText = foldRef
        ? this.editor.getSession().getDocument().getTextRange(foldRef.range)
        : getTokenTextForEncodedProperty(`{"err":"err"}`);
      const property = getPropertyFromTokenText(foldText);

      let oldQueryBlock = null;
      let newQueryBlock = null;
      if (idx >= buttonDiv.childNodes.length) {
        newQueryBlock = this.createQueryBlock(property);
      } else if (foldRef === this.lastHoveredFold) {
        oldQueryBlock = buttonDiv.childNodes[idx];
        newQueryBlock = this.createQueryBlock(property);
      } else {
        oldQueryBlock = newQueryBlock = buttonDiv.childNodes[idx];
        newQueryBlock.setProps({
          selected: property,
          selectorIcon: this.getPropertyButtonIcon(property) || ``,
          inputOnly: undefined,
        });
      }
      newQueryBlock.foldElement = fold;

      if (foldText === EMPTY_PROPERTY_TOKEN) {
        newQueryBlock.setProps({
          open: true,
        });
      }

      const foldRect = fold.getBoundingClientRect();
      newQueryBlock.style.top = `${Math.round(foldRect.top - contRect.top) + 6}px`;
      const offsetLeft = this.getPropertyButtonIcon(property) ? 11 : 15;
      newQueryBlock.style.left = `${foldRect.left - contRect.left + offsetLeft}px`;
      if (oldQueryBlock !== newQueryBlock) {
        if (oldQueryBlock === null) {
          buttonDiv.appendChild(newQueryBlock);
        } else {
          buttonDiv.replaceChild(newQueryBlock, oldQueryBlock);
        }
      }
    });

    while (folds.length < buttonDiv.childNodes.length) {
      buttonDiv.removeChild(buttonDiv.lastChild);
    }
  }

  private foldFromElement(elem: HTMLElement) {
    const renderer = this.editor.renderer;
    const foldRect = elem.getBoundingClientRect();
    const foldPos = this.editor.renderer.screenToTextCoordinates(
      foldRect.left + renderer.characterWidth / 2,
      foldRect.top + renderer.lineHeight / 2,
    );
    return this.editor.getSession().getFoldAt(foldPos.row, foldPos.column, 1);
  }

  getCurrentFunctionInfo(startingPos) {
    if (startingPos.column === 0 && this.editor.session.getLine(startingPos.row).length === 0) {
      startingPos = this.calculatePreviousPosition(startingPos);
    }
    const prevPos = this.calculatePreviousPosition(startingPos);
    const prevChar = this.editor.session.getLine(prevPos.row).charAt(prevPos.column);
    let parenPos = null;
    if (prevChar === `(`) {
      parenPos = startingPos;
    } else {
      parenPos = this.editor.session.$findOpeningBracket(`)`, startingPos, /text/);
      if (parenPos && prevChar === `)`) {
        parenPos = this.editor.session.$findOpeningBracket(`)`, parenPos, /text/);
      }
    }
    if (parenPos) {
      let ch = ` `;
      while (ch === ` `) {
        parenPos = this.calculatePreviousPosition(parenPos);
        ch = this.editor.session.getLine(parenPos.row).charAt(parenPos.column);
      }
      // Handle cases where parens aren't part of a function call, e.g.
      // STRING(A * (B + C))
      // STRING((A))
      const token = this.editor.session.getTokenAt(parenPos.row, parenPos.column);
      if (token?.type === `entity.name.function`) {
        return {token, parenPos};
      } else if (parenPos.row !== 0 && parenPos.column !== 0) {
        return this.getCurrentFunctionInfo(parenPos);
      }
    }
    return null;
  }

  private calculatePreviousPosition(pos) {
    if (pos.column !== 0) {
      return {
        row: pos.row,
        column: pos.column - 1,
      };
    } else if (pos.row === 0) {
      return {
        row: 0,
        column: 0,
      };
    } else {
      return {
        row: pos.row - 1,
        column: this.editor.session.getLine(pos.row - 1).length,
      };
    }
  }

  getAlphaLabel(composedPropertiesReverseLookup, newProperty): string {
    const stableStringifiedProperty = stringify(newProperty);
    if (stableStringifiedProperty in composedPropertiesReverseLookup) {
      return composedPropertiesReverseLookup[stableStringifiedProperty];
    }

    const alphaIndex = composedPropertiesReverseLookup[`highest_alpha_index`] + 1 || 0;
    composedPropertiesReverseLookup[`highest_alpha_index`] = alphaIndex;
    const alphaLabel = `_` + getAlphaLabelFromIndex(alphaIndex);
    composedPropertiesReverseLookup[stableStringifiedProperty] = alphaLabel;
    return alphaLabel;
  }

  getPropertyTokenSpecs(): PropertyTokenSpec[] {
    const propertyTokenSpecs: PropertyTokenSpec[] = [];

    const session = this.editor.getSession();
    const tokenIter = new TokenIterator(session, -1, 0);
    const propertiesReverseLookup = {};
    for (let token = tokenIter.stepForward(); token !== null; token = tokenIter.stepForward()) {
      if (!isPropertyToken(token.type)) {
        continue;
      }

      const tokenRange = tokenIter.getCurrentTokenRange();
      const tokenText = session.getDocument().getTextRange(tokenRange);
      const property = getPropertyFromTokenText(tokenText);
      const alphaLabel = this.getAlphaLabel(propertiesReverseLookup, property);

      propertyTokenSpecs.push({
        type: token.type,
        tokenText,
        range: tokenRange,
        alphaLabel,
      });
    }

    return propertyTokenSpecs;
  }

  getPropertyLabel(property: PropertyEntry): Translatable {
    if (this.canShowBorrowPropertyAction()) {
      // does not need dataGroups
      return getSelectedLabel({selected: property, dataGroups: []});
    } else {
      return computePropertyName(property, this.getContext(`i18n`)) || `Select a property`;
    }
  }

  recomputeFolds() {
    const {print} = this.getContext(`i18n`);

    const expectedFolds = [];
    for (const tokenSpec of this.getPropertyTokenSpecs()) {
      const {range: tokenRange, tokenText, alphaLabel} = tokenSpec;
      const property = getPropertyFromTokenText(tokenText);

      // The sizing here is based on how big the text that represents the property will be. The fold must
      // be at least large enough to hold the property name in the correct font plus some buffer for the
      // property icon. This then needs to be rounded up to the nearest multiple of the editor's monospace
      // font width (not rounding to font width would mis-align neighboring text in the editor).
      const label = print(this.getPropertyLabel(property));
      const textWidth = MeasureUtil.measureTextWidth(label, `600 14px ${mpFonts.fontFamilyPrimary}`);
      const characterWidth = this.editor.renderer.characterWidth || DEFAULT_CHARACTER_WIDTH;
      const iconChars = this.getPropertyButtonIcon(property) ? 3 : 0;
      const paddingChars = 2;
      const placeholderChars = paddingChars + iconChars + Math.ceil(textWidth / characterWidth);

      expectedFolds.push({
        placeholder: `${alphaLabel}`.padEnd(placeholderChars, ` `),
        range: tokenRange,
      });
    }

    const foldsInDocument = this.editor.getSession().getAllFolds();
    for (const foldInDocument of foldsInDocument) {
      let expected = false;
      for (const expectedFold of expectedFolds) {
        if (
          foldInDocument.placeholder === expectedFold.placeholder &&
          foldInDocument.range.isEqual(expectedFold.range)
        ) {
          expected = true;
          expectedFold.found = true;
          break;
        }
      }
      if (!expected) {
        this.editor.getSession().removeFoldReal(foldInDocument);
      }
    }

    for (const expectedFold of expectedFolds) {
      if (!expectedFold.found) {
        this.editor.getSession().addFoldReal(expectedFold.placeholder, expectedFold.range);
      }
    }
    this.lastHoveredFold = null;
  }

  private extractDisplayFormula() {
    const session = this.editor.getSession();
    const persistableFormula = [];
    const checkableFormula = [];
    // Errors should block saving, warnings should not
    const errors: AnnotationType[] = [];
    const warnings: AnnotationType[] = [];
    const properties: ComposedPropertiesMap = {};
    let startPos = {
      row: 0,
      column: 0,
    };

    for (const tokenSpec of this.getPropertyTokenSpecs()) {
      const {range: tokenRange, tokenText, alphaLabel} = tokenSpec;
      const property = getPropertyFromTokenText(tokenText);
      const textRange = new range.Range(startPos.row, startPos.column, tokenRange.start.row, tokenRange.start.column);
      persistableFormula.push(session.getDocument().getTextRange(textRange));
      checkableFormula.push(session.getDocument().getTextRange(textRange));
      startPos = tokenRange.end;

      properties[alphaLabel] = property;

      const createAnnotation = (message: string): AnnotationType => ({
        first_line: tokenRange.start.row,
        first_column: tokenRange.start.column,
        last_line: tokenRange.end.row,
        last_column: tokenRange.end.column,
        message,
      });

      switch (tokenSpec.type) {
        case `encoded_property`: {
          if (
            Object.entries(property).length === 0 &&
            [EMPTY_PROPERTY_TOKEN, PERSISTENT_EMPTY_PROPERTY_TOKEN].some((emptyToken) => emptyToken === tokenText)
          ) {
            errors.push(createAnnotation(this.t(`custom-property.error.select-value`)));
          }
          persistableFormula.push(` ${alphaLabel} `);
          checkableFormula.push(` ${alphaLabel}${` `.repeat(tokenText.length - alphaLabel.length - 2)} `);
          break;
        }

        case `comment.encoded_property`:
          errors.push(createAnnotation(this.t(`custom-property.error.commented-property`)));
          break;

        case `string.encoded_property`:
          errors.push(createAnnotation(this.t(`custom-property.error.quoted-property`)));
          break;
      }
    }
    this.composedProperties = properties;

    const lastRow = session.getDocument().getLength();
    const textRange = new range.Range(
      startPos.row,
      startPos.column,
      lastRow,
      session.getDocument().getLine(lastRow).length,
    );
    persistableFormula.push(session.getDocument().getTextRange(textRange));
    checkableFormula.push(session.getDocument().getTextRange(textRange));
    return {
      persistableFormula: persistableFormula.join(``),
      checkableFormula: checkableFormula.join(``),
      properties,
      errors,
      warnings,
    };
  }

  private populateDisplayFormula(displayFormula, composedProperties) {
    this.editor.setValue(displayFormula, -1);

    // replacements must be done from the end of the document to the start or they will overwrite eachother
    // we gather them in reverse order in 'replacements'
    const replacements = [];
    const tokenIter = new TokenIterator(this.editor.getSession(), -1, 0);
    for (let token = tokenIter.stepForward(); token !== null; token = tokenIter.stepForward()) {
      if (token.type === `variable.other` && token.value.toUpperCase() in composedProperties) {
        const tokenRange = tokenIter.getCurrentTokenRange().clone();
        const line = this.editor.getSession().getDocument().getLine(tokenRange.start.row);
        if (tokenRange.start.column > 0 && line[tokenRange.start.column - 1] === ` `) {
          tokenRange.start.column--;
        }
        if (tokenRange.end.column < line.length && line[tokenRange.end.column] === ` `) {
          tokenRange.end.column++;
        }
        replacements.unshift([
          tokenRange,
          getTokenTextForEncodedProperty(stringify(composedProperties[token.value.toUpperCase()])),
        ]);
      }
    }
    for (const replacement of replacements) {
      this.replaceRange(replacement[0], replacement[1]);
    }
  }

  private replaceRange(editorRange, text: string) {
    const startPos = {
      row: editorRange.start.row,
      column: editorRange.start.column,
    };
    this.editor.getSession().getDocument().remove(editorRange);
    this.editor.getSession().getDocument().insert(startPos, text);
  }

  private getPropertyButtonIcon(property: PropertyEntry): string {
    if (this.canShowBorrowPropertyAction()) {
      if (isEmpty(property)) {
        // if property hasn't been selected (`property` will be an empty object), show resource type icon before
        // the label "undefined"
        if (this.attr(`resource-type`) === QUERY_BUILDER_RESOURCE_TYPE_MAPPING[ResourceType.Event]) {
          return `event-attribute`;
        } else {
          return `profile-attribute`;
        }
      } else {
        return getSelectedLeftIcon(property);
      }
    } else {
      if (property && property.resourceType === ResourceType.Event) {
        return `event-attribute`;
      } else {
        return `profile-attribute`;
      }
    }
  }

  private async getSyntaxErrors(projectId, formula, variableSubstitutions, extractionErrors) {
    // If we could not properly extract the display formula there is no point in
    // syntax checking the result.
    if (extractionErrors.length > 0) {
      return extractionErrors;
    }

    const resp = await customPropertiesValidateClient.query({
      projectId,
      selector: formula,
      variableSubstitutions,
    });
    return resp.syntax_errors ? resp.syntax_errors : [];
  }

  private getVariableSubstitutions(properties: ComposedPropertiesMap) {
    return Object.entries(properties).reduce(
      (result, arr) => Object.assign(result, {[arr[0]]: {substitution: `unused`}}),
      {} as Record<string, {substitution: `unused`}>,
    );
  }

  // TODO opslead
  private scheduleSyntaxChecking() {
    // clear any previous marked errors, since contents have changed they may no longer be valid
    this.editor.getSession().setAnnotations([]);
    for (const entry of Object.entries(this.editor.getSession().getMarkers(true))) {
      const markerId = entry[0];
      const marker: any = entry[1];
      if (marker.clazz === CLASS_SYNTAX_ERROR) {
        this.editor.getSession().removeMarker(markerId);
      }
    }

    // clear previously scheduled syntax checking (putting in some delay stops us from
    // checking after every keystroke)
    clearTimeout(this.idleTimeout);
    this.idleTimeout = null;

    const rawFormula = this.editor.getValue();
    if (rawFormula !== ``) {
      const data = this.extractDisplayFormula();
      const projectId = this.getAttribute(`project-id`);

      const variableSubstitutions = this.getVariableSubstitutions(data.properties);
      this.idleTimeout = setTimeout(() => {
        (async () => {
          const currData = this.extractDisplayFormula();
          const currProps = this.getVariableSubstitutions(currData.properties);
          // Make sure if we do display errors they come from our most recent formula
          const syntaxErrors = await this.getSyntaxErrors(
            projectId,
            currData.checkableFormula,
            currProps,
            // We want to display errors and warnings as annotations
            [...currData.errors, ...currData.warnings],
          );

          // if the formula has changed since we issued our validation query then our
          // results are no longer useful, another query will already have been issued
          if (
            syntaxErrors.length > 0 &&
            data.checkableFormula === currData.checkableFormula &&
            stringify(Object.keys(variableSubstitutions)) === stringify(Object.keys(currProps))
          ) {
            const annotations = [];
            for (const err of syntaxErrors) {
              annotations.push({
                row: err[`first_line`],
                column: err[`first_column`],
                type: `error`,
                text: `\u2022 ` + err[`message`],
              });
              let errRange = new range.Range(
                err[`first_line`],
                err[`first_column`],
                err[`last_line`],
                err[`last_column`] + 1,
              );
              const fold = this.editor.getSession().getFoldAt(errRange.start.row, errRange.start.column, 1);
              if (fold) {
                errRange = fold.range.clone();
              }
              this.editor.getSession().addMarker(errRange, CLASS_SYNTAX_ERROR, `text`, true);
            }
            this.editor.getSession().setAnnotations(annotations);
          }
        })();
      }, 250);
    }
  }
}

export const EditorEl = defineAndMakeJsxFactory(`custom-property-ace-editor`, Editor);
